/*
  Дашборд — §3.1.3, §4.1.2.

  Отвечает на вопрос «что с деньгами» без единого клика. Порядок блоков —
  порядок важности: сначала что горит (эти деньги исчезнут сами), потом
  взыскиваемая база, потом остальное.

  Блоки, которых у системы пока нет, показаны отдельным списком с объяснением.
  Их не заменяют нулями: ноль в строке «вернули за месяц» — это ложь, которая
  вскроется на первой сверке с бухгалтерией (§3.1.4).

  09.09.2026: блок «что принёс Арбитр» и строка радара переехали из этого
  списка на сам экран. Обе величины считаются с 05.09 — счётчиками окупаемости
  и радаром, — а список продолжал обещать их «с этапом 3».
*/

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client.js'
import { accounts, accountsDative, date, money, plural } from '../api/format.js'
import { Empty, Loading, MoneyCard, Notice } from '../components/Common.jsx'
import CounterTile from '../components/CounterTile.jsx'
import PortfolioBar from '../components/PortfolioBar.jsx'
import RegistryFindingsBanner from '../components/RegistryFindingsBanner.jsx'
/* Перечень сегментов общий с «Отчётами» (вкладка «Полоски»): одна и та же
   полоса на двух экранах обязана вести в один и тот же отбор реестра. */
import { portfolioSegments } from '../components/portfolioSegments.js'

const findings = (count) => plural(count, 'повод', 'повода', 'поводов')

export default function DashboardPage({ onBurning, profile }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api
      .dashboard()
      .then((summary) => {
        setData(summary)
        onBurning(summary.burning.accounts)
      })
      .catch(setError)
  }, [onBurning])

  if (error) return <Notice error={error} />
  if (!data) return <Loading what="Считаем портфель" />

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Что происходит с деньгами</h1>
          <div className="head__note">
            Расчёт на {date(data.as_of)} по правовому пакету {data.legal_package_version}
            {data.uncalibrated
              ? ' · оценка возврата предварительная: коэффициенты не откалиброваны первой волной'
              : ''}
          </div>
        </div>
      </header>

      {/*
        🔴 Верхняя полоса экрана - два столбца (29.09.2026, указание владельца
        «перенеси полоску вправо вверх, где пустое пространство»). Плашки тревог
        ограничены длиной строки (`--measure`), и справа от них оставалась
        половина экрана пустой; полоса портфеля заняла её, не прибавив экрану ни
        пикселя высоты.

        Порядок в разметке - тревоги, потом полоса: читалка экрана и обход с
        клавиатуры идут по ней, а не по картинке, и «горит срок давности» обязано
        прозвучать первым.
      */}
      <div className="lead">
        <div className="lead__alerts">
          {data.burning.accounts > 0 ? (
            <div className="notice notice--error notice--wide">
              <div className="notice__what">
                Горит срок давности: {money(data.burning.amount)} по{' '}
                {accountsDative(data.burning.accounts)}
                {data.burning.nearest_date
                  ? `, ближайший выпадает ${date(data.burning.nearest_date)}`
                  : ''}
              </div>
              <div className="notice__do">
                Эти деньги исчезнут сами, без участия должника.{' '}
                <Link to="/registry?limitation=expiring_soon">Открыть в реестре</Link>
              </div>
            </div>
          ) : null}

          {/* 27.10.2026, вариант Б: находки реестра банкротств держат подачу, и об этом
              говорят на первом экране - сразу после «горит», до радара. */}
          <RegistryFindingsBanner profile={profile} />

          {/*
            Радар стоит сразу под тем, что горит, и не красится тревогой: это не
            предупреждение, а поводы вернуться к делам. Сами находки на дашборд не
            идут - за ними человек уходит в раздел, где у каждой названо действие.
          */}
          {data.radar.findings > 0 ? (
            <Notice kind="ok" wide>
              <div className="notice__what">
                Радар нашёл {findings(data.radar.findings)} на {money(data.radar.amount)}
                {data.radar.burning > 0 ? `, из них ${data.radar.burning} срочных` : ''}
              </div>
              <div className="notice__do">
                <Link to="/radar">Открыть радар</Link>
              </div>
            </Notice>
          ) : null}
        </div>

        <PortfolioBar segments={portfolioSegments(data)} title="Весь портфель: где лежат деньги" />
      </div>

      {/*
        🔴 Каждая плашка ведёт в реестр, отобранный РОВНО тем условием, по
        которому она посчитана (29.09.2026, указание владельца). Пары
        «плашка - отбор» проверены по коду обоих: маршрут, работа и сгоревшая
        часть считаются на сервере одними и теми же функциями для дашборда и для
        реестра, поэтому число на плашке совпадает с «Найдено N счетов».

        Приблизительного отбора здесь быть не должно: человек, увидевший на
        плашке одно число, а в реестре другое, перестаёт верить обоим экранам.
      */}
      <div className="grid">
        <MoneyCard
          label="Взыскивать имеет смысл"
          value={data.collectable.amount}
          note={`${accounts(data.collectable.accounts)} · ожидаемый возврат ${money(data.expected_return)}`}
          to="/registry?route=court"
        />
        <MoneyCard
          label="В работе"
          value={data.in_progress.amount}
          note={accounts(data.in_progress.accounts)}
          to="/registry?work=in_progress"
        />
        <MoneyCard
          label="Сгорело по давности"
          value={data.already_expired.amount}
          note={`${accounts(data.already_expired.accounts)} · на сегодня, вернуть нельзя; в том числе сгоревшее до подключения - ниже`}
          tone="burn"
          to="/registry?expired=some"
        />
        <MoneyCard
          label="Невыгодно судиться"
          value={data.unprofitable.amount}
          note={`${accounts(data.unprofitable.accounts)} · пойдут напоминания`}
          to="/registry?route=pretrial_batch"
        />
      </div>

      {/*
        Блок, ради которого директор откроет экран при продлении лицензии
        (§3.1.3). Период назван датой начала, а не словами «с начала года»:
        через год та же надпись означала бы другой отрезок.
      */}
      <section className="section">
        <h2 className="section__title">Что принёс Арбитр с {date(data.brought_since)}</h2>
        <div className="grid grid--even">
          <CounterTile counter={data.brought.saved_from_expiry} tone="good" />
          <CounterTile counter={data.brought.found_by_radar} tone="good" />
          <CounterTile counter={data.brought.burned_before_us} tone="loss" />
        </div>
        <div className="money__note">
          Расход на Арбитр ЖКХ и чистый эффект - в <Link to="/reports">отчётах</Link>: там же
          выбирается период.
        </div>
      </section>

      <section className="section">
        <h2 className="section__title">Воронка стадий</h2>
        <div className="funnel">
          {data.funnel
            .filter((step) => step.accounts > 0)
            .map((step) => (
              <Link key={step.stage} to={`/registry?stage=${step.stage}`} className="funnel__step">
                <span className="funnel__num">{step.accounts}</span>
                {step.title}
              </Link>
            ))}
          {data.total_accounts === 0 ? (
            <Empty title="Портфель пуст">
              Загрузите выгрузку из учётной системы - Арбитр ЖКХ разберёт её, посчитает долг, пени и
              сроки давности по каждому счёту и покажет, кого подавать первыми.{' '}
              <Link to="/import">Загрузить выгрузку</Link>
            </Empty>
          ) : null}
        </div>
      </section>

      {data.excluded.accounts > 0 || data.needs_human.accounts > 0 || data.lawsuit.accounts > 0 ? (
        <section className="section">
          <h2 className="section__title">Отдельного разбора</h2>
          <div className="grid">
            {/* 🔴 29.09.2026. Маршрут «только исковое» до этого дня не был
                показан на первом экране НИГДЕ, и деньги таких счетов пропадали
                с него целиком. Плашка стоит здесь, а не в первом ряду: счетов
                этого рода обычно нет вовсе, и пустая пятая плашка занимала бы
                первый экран каждый день ради редкого случая. Соседи по разделу
                такие же - показываются, только когда есть о чём говорить. */}
            {data.lawsuit.accounts > 0 ? (
              <MoneyCard
                label="Только исковое"
                value={data.lawsuit.amount}
                note={`${accounts(data.lawsuit.accounts)} · пошлина вдвое дороже приказной`}
                to="/registry?route=lawsuit"
              />
            ) : null}
            {data.excluded.accounts > 0 ? (
              <MoneyCard
                label="Исключены из взыскания"
                value={data.excluded.amount}
                note={`${accounts(data.excluded.accounts)} · найдена процедура банкротства`}
                to="/registry?route=excluded"
              />
            ) : null}
            {data.needs_human.accounts > 0 ? (
              <MoneyCard
                label="Нужен человек"
                value={data.needs_human.amount}
                note={`${accounts(data.needs_human.accounts)} · Арбитр ЖКХ разбирать отказался`}
                to="/registry?route=manual_review"
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {/* 22.09.2026. Раздел показывается только когда в нём есть хоть одна
          строка. Список пуст с 10.09.2026 (`dashboard_service.MISSING_BLOCKS`),
          и заголовок висел над пустотой - обещание, под которым ничего нет,
          читается как поломка экрана. Убирать раздел из кода нельзя: сервер
          по-прежнему присылает список, и следующий честный пробел должен
          показаться сам, без правки кабинета. */}
      {data.unavailable.length > 0 ? (
        <section className="section">
          <h2 className="section__title">Чего здесь пока нет</h2>
          <div className="grid">
            {data.unavailable.map((block) => (
              <div className="card" key={block.title}>
                {/* Здесь у метки нет своей суммы, поэтому вторую строку под неё
                    резервировать незачем: `money__label--flat` снимает высоту,
                    которая в ряду денег держит цифры на одном уровне. */}
                <div className="money__label money__label--flat">{block.title}</div>
                <p>{block.explanation}</p>
                <div className="money__note">Появится вместе с {block.appears_with}.</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </>
  )
}
